import { format, isValid, parseISO } from 'date-fns';
import { enUS, es, pt } from 'date-fns/locale';
import type { RefObject } from 'react';
import { registerLocale } from 'react-datepicker';

import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { useLoadBundleCssOnce } from '#/zustand-stores/loadCssBundleStore';

import { DatePickerDateInput } from './DatePickerDateInput';
import { DatePickerPopperContainer } from './DatePickerPopperContainer';
import { ReactDatePickerLoadable } from './ReactDatePickerLoadable';
import { createLocalDateString } from './helpers/createLocalDateString';

//:horizon://import 'react-datepicker/dist/react-datepicker.css';

registerLocale(I18nLocale.EN, enUS);
registerLocale(I18nLocale.ES, es);
registerLocale(I18nLocale.PT, pt);

type Props = {
  className?: string;
  id?: string;
  name?: string;
  required?: boolean;
  value: string | null | undefined;
  placeholder?: string;
  onChange: (date: string | null) => void;
  disablePastDates?: boolean;
  disableFutureDates?: boolean;
  strictParsing?: boolean;
  dateFormat?: string;
  isTimeZoneAware?: boolean;
  showMonthYearPicker?: boolean;
  qaId?: string;
  ref?: RefObject<HTMLInputElement | null>;
};

export function DatePicker({
  className,
  value,
  onChange,
  disablePastDates,
  disableFutureDates,
  strictParsing = true,
  dateFormat,
  isTimeZoneAware,
  showMonthYearPicker,
  qaId,
  ref,
  ...rest
}: Props) {
  const { locale } = useI18n();
  const datetimeProps = {
    ...rest,
    ref,
    qaId,
    autoComplete: 'off',
  };

  useLoadBundleCssOnce('reactDatepicker');

  const parsedValue = value ? parseISO(value) : null;
  return (
    <ReactDatePickerLoadable
      className={className}
      customInput={<DatePickerDateInput inputProps={datetimeProps} />}
      shouldCloseOnSelect
      locale={locale}
      name={datetimeProps.name}
      dateFormat={dateFormat ?? 'P'}
      showMonthYearPicker={showMonthYearPicker}
      minDate={disablePastDates ? new Date() : undefined}
      maxDate={disableFutureDates ? new Date() : undefined}
      popperContainer={DatePickerPopperContainer}
      strictParsing={strictParsing}
      selected={parsedValue}
      onChange={(selectedDate: Date | null) => {
        if (selectedDate && isValid(selectedDate) && isTimeZoneAware) {
          onChange(createLocalDateString(selectedDate));
        } else if (selectedDate && isValid(selectedDate)) {
          const dateString = format(selectedDate, 'yyyy-MM-dd');
          onChange(dateString);
        } else {
          onChange(null);
        }
      }}
    />
  );
}
